/**
 * BettingTipBanner
 * Dismissable info banner for contextual betting tips.
 * Shows at key moments (first generation, first track, new user hero).
 * Dismissed tips are tracked in localStorage; once all seen, showBettingTips
 * is auto-disabled via the preferences API.
 *
 * Design: Gradient background, glowing icon circle, left accent stripe,
 * animated entrance — matching the app's premium aesthetic.
 */

import { useAuth } from '@contexts/AuthContext';
import { allTipsSeen, type BettingTip, shouldShowTip } from '@parlay-picker/shared/bettingTips';
import { Lightbulb, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface BettingTipBannerProps {
  tip: BettingTip;
}

/** Read seen tip IDs from localStorage */
function getSeenTips(): string[] {
  try {
    const raw = localStorage.getItem('seenBettingTips');
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/** Write seen tip IDs to localStorage */
function setSeenTips(ids: string[]): void {
  localStorage.setItem('seenBettingTips', JSON.stringify(ids));
}

export function BettingTipBanner({ tip }: BettingTipBannerProps): ReactElement | null {
  const { user, updatePreferences } = useAuth();
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    const seen = getSeenTips();
    const show = shouldShowTip(tip.id, user?.preferences?.showBettingTips, seen);
    setVisible(show);
  }, [tip.id, user?.preferences?.showBettingTips]);

  const handleDismiss = useCallback(async () => {
    setDismissing(true);

    // Wait for exit animation
    setTimeout(async () => {
      setVisible(false);

      // Track this tip as seen
      const seen = getSeenTips();
      if (!seen.includes(tip.id)) {
        const updated = [...seen, tip.id];
        setSeenTips(updated);

        // Auto-disable if all tips have been seen
        if (allTipsSeen(updated)) {
          await updatePreferences({ showBettingTips: false });
        }
      }
    }, 200);
  }, [tip.id, updatePreferences]);

  if (!visible) return null;

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border border-theme-primary/20
        bg-linear-to-r from-theme-primary/10 via-theme-primary/5 to-transparent
        transition-all duration-200
        ${dismissing ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0 animate-fade-in-up'}
      `}
    >
      {/* Left accent stripe */}
      <div className="absolute left-0 top-0 bottom-0 w-0.75 bg-theme-primary" />

      <div className="flex items-start gap-3 pl-4 pr-3 py-3.5">
        {/* Glowing icon circle */}
        <div className="w-9 h-9 rounded-full bg-theme-primary/15 flex items-center justify-center shrink-0 mt-0.5">
          <Lightbulb className="w-4.5 h-4.5 text-theme-primary" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-theme-text tracking-wide mb-0.5">{tip.title}</p>
          <p className="text-xs text-theme-text-secondary leading-relaxed">{tip.body}</p>
          <Link
            to="/faq#smart-betting-guide"
            className="text-xs text-theme-primary hover:text-theme-primary-hover mt-1.5 inline-block font-medium transition-colors"
          >
            {tip.linkText} →
          </Link>
        </div>

        {/* Dismiss */}
        <button
          type="button"
          onClick={handleDismiss}
          className="text-theme-text-tertiary hover:text-theme-text hover:bg-theme-primary/10 transition-all shrink-0 rounded-lg p-1.5 -mt-0.5 -mr-1"
          aria-label="Dismiss tip"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
